import { Box, Typography } from '@mui/material';
import { id } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import classSet from 'react-classset';
import { useFormContext } from 'react-hook-form';

import { AssetSource } from '@/enums';
import type { Item } from '@/models';
import { FuelEntityType, FuelPortalService } from '@/services';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';

import ImageUploader from '../SeriesContainer/ImageUploader';
import type { VNLibraryArgs } from '../VNLibrary/VNLibrary';

interface Props {
  type: 'video' | 'image' | 'LiveEvent';
  uploadThumbnail?: (file: File) => void;
}

const ASPECT_RATIOS = {
  square: 1,
  landscape: 1.5,
  poster: 0.66,
  width: '100%',
};

const DisplaySettingsForm = ({ type, uploadThumbnail }: Props) => {
  const { alert } = useAppUtils();
  const { watch, getValues, setValue } = useFormContext();
  const fileInput = useRef<HTMLInputElement | null>(null);
  // const activeFile = watch(`files.${index}`);
  // const formPath = `files.${index}`;
  const source = watch(`source`);
  const tempThumbnailBlob = watch('originalAssetData.data.thumbnail.tempThumbnailBlob');
  const previewUri = watch('originalAssetData.data.thumbnail.previewUri');
  const aspectRatio = ASPECT_RATIOS.landscape;
  const [imageUrl, setImageUrl] = useState(null);
  const isReadOnly = source === AssetSource.ClipGroup;

  useEffect(() => {
    // console.log('DisplaySettingsForm previewUri, tempThumbnailBlob changed', { previewUri, tempThumbnailBlob });
    const src = getAssetThumbnail();
    setImageUrl(src);
  }, [previewUri, tempThumbnailBlob]);

  useEffect(() => {
    // console.log('DisplaySettingsForm type changed', { type, form: getValues() });
    if (type) {
      fetchAssets();
    }
  }, [type]);

  const getAssetThumbnail = () => {
    let src: string | undefined = '/static/images/thumbnail.png';
    const myAsset: Item = getValues('originalAssetData');
    if (myAsset != null) {
      if (!clientUtils.isNullOrWhitespace(myAsset?.data?.thumbnail?.tempThumbnailBlob)) {
        src = myAsset?.data?.thumbnail?.tempThumbnailBlob;
      } else if (!clientUtils.isNullOrWhitespace(myAsset?.data?.thumbnail?.previewUri)) {
        src = myAsset?.data?.thumbnail?.previewUri;
      }
    }
    return src;
  };

  const onItemSelected = (args: VNLibraryArgs) => {
    // console.log('onItemSelected', { args });
    if (args?.field) {
      setValue(args.field, args.item);

      // update linkedAssets accordingly
      let linkedAssets = getValues('linkedAssets');
      const displaySettings = getValues('displaySettings');
      const pureField = args.field.substring(args.field.lastIndexOf('.') + 1);
      displaySettings[pureField] = args?.item?.id || null;
      linkedAssets = clientUtils.populateLinkedAssetsWithDiplaySettings(linkedAssets, displaySettings);
      setValue('linkedAssets', linkedAssets);
    }
  };
  const items = [
    { field: `displaySettings.background`, title: 'Background' },
    {
      field: `displaySettings.thumbnail`,
      title: source === 'Ingest' || source === 'ExternalClip' ? 'Hi Res Thumbnail' : 'Thumbnail',
    },
    { field: `displaySettings.poster`, title: 'Poster' },
    { field: `displaySettings.logo`, title: 'Logo' },
    { field: `displaySettings.badge`, title: 'Badge' },
  ];

  const getShowItemFn = () => {
    let fieldsToHide: string[] = [];

    const isClipGroupSource = source === AssetSource.ClipGroup;

    switch (type) {
      case 'image':
        // Hide all the display settings fields for images now.
        fieldsToHide = [
          'displaySettings.background',
          'displaySettings.thumbnail',
          'displaySettings.logo',
          'displaySettings.badge',
          'displaySettings.poster',
        ];
        break;
      case 'LiveEvent':
        fieldsToHide = ['displaySettings.logo', 'displaySettings.badge', 'displaySettings.poster'];
        break;
      default:
        fieldsToHide = [];
        break;
    }
    if (isClipGroupSource) {
      fieldsToHide.push(
        'displaySettings.thumbnail'
        // add whatever fields you want to restrict
      );
    }
    return (field: string) => !fieldsToHide.includes(field);
  };
  const showItem = getShowItemFn();

  const fetchAssets = async () => {
    let itemLookup = getValues(`tmp.itemLookup`);
    const thumbnail = watch(`displaySettings.thumbnail`);
    if (!itemLookup) {
      itemLookup = {};
    }
    let displaySettings = getValues(`displaySettings`);
    if (displaySettings == null) {
      displaySettings = {};
    }
    const imageIds = Object.entries(displaySettings)
      .filter(([key, value]) => key !== 'backgroundColor' && value !== null)
      .map(([_, value]) => value);

    if (imageIds.length > 0 && id) {
      const rawAssets: Item[] = await FuelPortalService.batchGet(FuelEntityType.Clip, imageIds);
      // console.log('rawAssets', { rawAssets });
      for (let i = 0; i < rawAssets.length; i++) {
        itemLookup[rawAssets[i].id] = rawAssets[i];
      }
      for (const [key, value] of Object.entries(displaySettings)) {
        // console.log('initData', { key, value });

        // eslint-disable-next-line no-continue
        if (!value) continue; // Skip null values or objects without an 'id'

        const item = itemLookup[value];
        // eslint-disable-next-line no-continue
        if (!item) continue;
      }
    }
    // show the thumbail image generated by the system if none is defined by the user.
    // if (!thumbnail) {
    //   const originalAssetData = watch(`originalAssetData`);
    //   const assetId = originalAssetData?.id;
    //   if (assetId) {
    //     setValue(`displaySettings.thumbnail`, assetId);
    //     itemLookup[originalAssetData.id] = originalAssetData;
    //   }
    // }
    setValue(`tmp.itemLookup`, itemLookup);
  };

  const processFileChange = (fileList: FileList) => {
    if (fileList.length === 0) return;

    // only supporting single file upload...multiple files are possible with the drag and drop, so just take the first file...
    const file = fileList.item(0);
    // console.log('file', file);
    // setThumbnailFile(file);
    if (uploadThumbnail) uploadThumbnail(file);
    // if (fileInput && fileInput.current) fileInput.current.value = '';
  };

  const onBrowseButtonClick = () => {
    // console.log('onBrowseButtonClick');
    const formData = getValues();
    if (formData?.originalAssetData?.sourceType === 'NestedChannel') {
      alert(
        'To change the thumbnail for a nested channel, you must make the change at the channel level on the Channel > Info tab.',
        'warning'
      );
      return;
    }

    // `current` points to the mounted file input element
    if (fileInput && fileInput.current) {
      fileInput.current.click();
    }
  };

  const removeImage = () => {
    setValue('image', null);
    setValue('tmp.imagePreviewUrl', null);
  };

  return type ? (
    <div className="widget-container">
      {getValues('id') != null && (
        <Box className="thumbnail-image-uploader">
          <Box>
            <Box sx={{ display: 'inline-block', width: '250px' }}>
              <input
                ref={fileInput}
                accept="image/png"
                type="file"
                id="image-upload"
                style={{ display: 'none' }}
                onChange={(e) => processFileChange(e.target.files)}
              />
              <div style={{ aspectRatio }}>
                <div
                  className={classSet({
                    'image-box': true,
                    image: true,
                    filled: imageUrl != null,
                    'read-only': isReadOnly,
                  })}
                >
                  {imageUrl != null && (
                    <img
                      className="selected-thumbnail"
                      src={imageUrl || '/static/images/thumbnail.png'}
                      alt="Selected Thumbnail"
                    />
                  )}
                  <div className="stock-image-container">
                    {!isReadOnly && (
                      <svg onClick={onBrowseButtonClick} className="stock-image add" viewBox="0 0 24 24" fill="none">
                        <path className="main-stroke" d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill="currentColor" />
                      </svg>
                    )}
                    {!isReadOnly && (
                      <svg
                        onClick={removeImage}
                        className="stock-image trashcan"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          className="main-stroke"
                          d="M17 4.875V6.66667H7V4.875H9.48322L10.2215 4H13.7785L14.5168 4.875H17ZM7.7047 18.2083V7.54167H16.2953V18.2083C16.2953 18.6806 16.1499 19.0972 15.8591 19.4583C15.5682 19.8194 15.2327 20 14.8523 20H9.14765C8.76734 20 8.43177 19.8194 8.14094 19.4583C7.85011 19.0972 7.7047 18.6806 7.7047 18.2083Z"
                          fill="currentColor"
                        />
                      </svg>
                    )}
                    <svg className="stock-image image" viewBox="0 0 160 160" fill="none">
                      <g>
                        <path
                          className="main-stroke"
                          d="M49.7778 30.2222C60.5775 30.2222 69.3333 39.3761 69.3333 50.6667C69.3333 61.9572 60.5775 71.1111 49.7778 71.1111C38.9781 71.1111 30.2222 61.9572 30.2222 50.6667C30.2222 39.3761 38.9781 30.2222 49.7778 30.2222ZM49.7778 60.8889C55.1776 60.8889 59.5556 56.3119 59.5556 50.6667C59.5556 45.0215 55.1776 40.4444 49.7778 40.4444C44.3779 40.4444 40 45.0215 40 50.6667C40 56.3119 44.3779 60.8889 49.7778 60.8889ZM150 0C155.528 0 160 4.4723 160 10V150C160 155.528 155.528 160 150 160H10C4.4723 160 0 155.528 0 150V10C0 4.4723 4.4723 0 10 0H150ZM10 145C10 146.388 10.5625 147.638 11.4751 148.545C10.5677 147.638 10 146.388 10 145ZM150 145V129.99L113.55 93.5393C111.597 91.5867 108.432 91.5867 106.48 93.5393C106.48 93.5393 82.4025 117.616 77.0855 122.939C71.7677 128.252 68.1856 128.174 62.9455 122.939C57.7053 117.695 53.5502 113.539 53.5502 113.539C51.5976 111.587 48.4327 111.587 46.4801 113.539L11.4757 148.545C12.3779 149.443 13.6233 150 15.0006 150H145.001C147.763 150 150.001 147.763 150.001 145L150 145ZM150 115.85V15C150 12.2373 147.763 10 145 10H15C12.2373 10 10 12.2373 10 15V135.88L42.9452 102.939C46.8502 99.0243 53.1801 99.0243 57.0852 102.939L70.0151 115.865L102.945 82.9387C106.851 79.0237 113.18 79.0237 117.085 82.9387L150 115.85Z"
                          fill="currentColor"
                        />
                      </g>
                      <defs>
                        <clipPath>
                          <rect width="160" height="160" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                    <svg className="stock-image video" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g>
                        <path
                          className="main-stroke"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M15.1145 70.942V149.708H149.708V70.942H15.1145ZM4.82227 69.1143C4.82227 64.4392 8.61221 60.6496 13.287 60.6496H151.535C156.21 60.6496 160 64.4392 160 69.1143V151.535C160 156.21 156.21 160 151.535 160H13.287C8.61221 160 4.82227 156.21 4.82227 151.535V69.1143Z"
                          fill="#E1E1E1"
                        />
                        <path
                          className="main-stroke"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M139.512 0.300051C143.989 -0.919004 148.657 1.68142 149.914 6.19777L149.917 6.20746L153.466 19.0558C153.466 19.0569 153.466 19.0581 153.467 19.0592C154.724 23.5854 152.048 28.2372 147.545 29.4666C147.543 29.467 147.541 29.4675 147.54 29.468L14.2761 66.0762L14.265 66.0793C9.78796 67.2983 5.12012 64.6979 3.86317 60.1815L3.86048 60.1718L0.311733 47.3235C0.311161 47.3215 0.310591 47.3194 0.310021 47.3174C-0.945501 42.7932 1.72876 38.1441 6.22933 36.9137C6.23219 36.9129 6.23506 36.9121 6.23792 36.9113L139.501 0.303099L139.512 0.300051ZM140.482 10.7073L10.7214 46.3533L13.2953 55.672L143.056 20.026L140.482 10.7073ZM143.55 21.8154C143.549 21.8122 143.548 21.8089 143.548 21.8057L143.55 21.8154Z"
                          fill="#E1E1E1"
                        />
                        <path
                          className="main-stroke"
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M73.8926 96.0304V127.621L97.6719 111.826L73.8926 96.0304ZM76.7588 85.5782C71.1451 81.8464 63.6003 85.844 63.6003 92.6178V131.034C63.6003 137.831 71.1725 141.783 76.7585 138.074L105.676 118.865C110.703 115.514 110.703 108.138 105.676 104.787L76.7622 85.5804C76.7617 85.5801 76.7627 85.5807 76.7622 85.5804C76.7616 85.58 76.7595 85.5786 76.7588 85.5782Z"
                          fill="#E1E1E1"
                        />
                      </g>
                      <defs>
                        <clipPath>
                          <rect width="160" height="160" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                </div>
              </div>
            </Box>
            <Typography variant="h6">Thumbnail</Typography>
            <Typography variant="body2" sx={{ color: '#4B5563', fontSize: '12px' }}>
              1280 x 720 | .png, .jpg, .jpeg
            </Typography>
          </Box>
        </Box>
      )}
      {items.map(
        ({ field, title }) =>
          showItem(field) && (
            <ImageUploader
              key={field}
              field={field}
              title={title}
              onItemSelected={onItemSelected}
              watchField="tmp.itemLookup"
              readOnly={isReadOnly}
            />
          )
      )}
    </div>
  ) : null;
};

export default DisplaySettingsForm;
